import { Autocomplete } from '@material-ui/lab';
import { CloseIcon, TextInput } from '@room-match/shared-ui-components';
import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { IAddress, IAddressRequestPayload } from 'shared/interfaces/IAddress';
import {
  IGoogleGeocoderAddressComponents,
  IGoogleGeocoderAttributes,
} from 'shared/interfaces/utils/IGoogleGeocoderApi';
import { countryStateApiService } from 'shared/services/countryStateApiService';
import { googleMapsService } from 'shared/services/googleMapsService';
import { locationService } from 'shared/services/locationService';
import { mappedCity, mappedCountry, mappedState } from 'shared/utils/countryStateMapper';
import { getErrorMessage } from 'shared/utils/room-shared';
import {
  ActionsContainer,
  CloseButton,
  Content,
  CustomButton,
  CustomDialog,
  CustomDialogContent,
  Header,
  useStyles,
} from './LocationModal.styles';
import { useLocationForm } from './LocationModalForm';

import { CircularProgress, useTheme } from '@material-ui/core';
import { COLORS } from 'shared/constants/COLORS';
import WorkingLocationModal from './WorkingLocationModal';

type Props = {
  data: IAddress | null;
  open: boolean;
  type: 'personal' | 'work';
  ifNoAddresses?: boolean;
  onClose: (instance: boolean) => void;
  setSelectedLocation?: React.Dispatch<React.SetStateAction<IAddress | null>>;
  country?: string;
};

const { getCountries, getStatesByCountry, getCitiesByStateAndCountry } = countryStateApiService();
const { createLocation, updateLocation } = locationService();
const { geolocate } = googleMapsService();
const LocationModal: React.FC<Props> = ({
  country,
  data,
  open,
  type,
  ifNoAddresses = false,
  onClose,
  setSelectedLocation,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const { mutateAsync, isLoading } = createLocation();
  const { mutateAsync: updateMutateAsync, isLoading: updateIsLoading } = updateLocation();
  const { mutateAsync: geolocateMutateAsync, isLoading: isGeolocateLoading } = geolocate();
  const queryClient = useQueryClient();
  const [addressList, setAddressList] = useState<any[]>([]);

  console.log({ data });

  const handleSubmit = async (values: IAddressRequestPayload) => {
    if (setSelectedLocation && values) {
      console.log({ values });

      setSelectedLocation({
        type: '',
        id: '',
        attributes: {
          primary: true,
          type: 'work',
          ...values,
          ...(values.zip_code !== '' && { zip_code: values.zip_code }),
        },
      });
      return onClose(false);
    }

    if (data) {
      updateMutateAsync(
        { locationId: data.id, payload: { ...values } },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(['locations'], {
              refetchInactive: true,
            });
            queryClient.invalidateQueries(['accounts'], {
              refetchInactive: true,
            });
            onClose(false);
          },
        },
      );
    } else {
      mutateAsync(
        { ...values, type },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(['locations'], {
              refetchInactive: true,
            });
            queryClient.invalidateQueries(['accounts'], {
              refetchInactive: true,
            });
            onClose(false);
          },
        },
      );
    }
    setAddressList([]);
  };

  const { form } = useLocationForm({
    data: data,
    // type: type,
    onSubmit: handleSubmit,
  });

  // useEffect(() => {
  //   if (type === 'general') {
  //     form.setFieldValue('type', 'Work');
  //     if (ifNoAddresses) {
  //       form.setFieldValue('address_type', 'primary_address');
  //     } else {
  //       form.setFieldValue('address_type', 'other_address');
  //     }
  //   } else form.setFieldValue('type', 'Personal');
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [type]);

  const geocode = async (zipCode: string) => {
    await geolocateMutateAsync(
      { zip_code: zipCode, country_code: form.values.country_code },
      {
        onSuccess: (response: any) => {
          queryClient.invalidateQueries(['geocode'], {
            refetchInactive: true,
          });
          const res = response.results;
          const arr: any[] = [];
          res
            .filter((r: IGoogleGeocoderAttributes) => r.types.includes('postal_code'))
            .map((i: IGoogleGeocoderAttributes) => {
              const obj: any = {
                key: i.address_components
                  .filter((v: IGoogleGeocoderAddressComponents) => v.types.includes('postal_code'))
                  .map((i: IGoogleGeocoderAddressComponents) => i.short_name)
                  .toString(),
                address1: i.formatted_address,
                state_code: i.address_components
                  .filter((v: IGoogleGeocoderAddressComponents) => v.types.includes('administrative_area_level_1'))
                  .map((i: IGoogleGeocoderAddressComponents) => i.short_name)
                  .toString(),
                city_suburb:
                  i.address_components.filter((v: IGoogleGeocoderAddressComponents) =>
                    v.types.includes('administrative_area_level_2'),
                  ).length > 0
                    ? i.address_components
                        .filter((v: IGoogleGeocoderAddressComponents) =>
                          v.types.includes('administrative_area_level_2'),
                        )
                        .map((i: IGoogleGeocoderAddressComponents) => i.short_name)
                        .toString()
                    : i.address_components
                        .filter((v: IGoogleGeocoderAddressComponents) => v.types.includes('locality'))
                        .map((i: IGoogleGeocoderAddressComponents) => i.short_name)
                        .toString(),
              };
              arr.push(obj);
            });
          setAddressList(arr);
        },
      },
    );
  };

  const { data: countriesData } = getCountries();
  const { data: statesData } = getStatesByCountry(form.values.country_code || '', {
    enabled: !!form.values.country_code,
  });

  const { data: citiesData } = getCitiesByStateAndCountry(
    form.values.country_code || '',
    form.values.state_code || '',
    { enabled: !!form.values.country_code && !!form.values.state_code },
  );

  console.log({ form });

  return (
    <CustomDialog
      type={type}
      open={open}
      onClose={() => onClose(false)}
      maxWidth="sm"
      fullWidth
      aria-labelledby="simple-dialog-title"
    >
      <CustomDialogContent>
        <CloseButton>
          <CloseIcon
            htmlColor={theme.palette.type === 'dark' ? COLORS.WHITE : COLORS.BLACK}
            style={{ width: '14px' }}
            onClick={() => onClose(false)}
          />
        </CloseButton>
        <Header>
          <p style={{ fontSize: 24 }}>{data ? 'Edit' : 'Add'} Location</p>
        </Header>
        {type === 'personal' ? (
          <Content>
            {/* <Autocomplete
            options={addressTypeList || []}
            getOptionSelected={(options, values) => options.value === values.value}
            getOptionLabel={(option) => option.value}
            value={
              ifNoAddresses
                ? {
                    key: form.values.address_type || '',
                    value:
                      form.values.address_type === 'physical_address'
                        ? 'Physical'
                        : form.values.address_type === 'other_address'
                        ? 'Other'
                        : form.values.address_type === 'postal'
                        ? 'Postal'
                        : '',
                  }
                : {
                    key: 'Primary_address',
                    value: 'Primary',
                  }
            }
            fullWidth
            onChange={(e, newValue) => {
              if (newValue) {
                form.setFieldValue('address_type', newValue.key);
                form.handleChange(newValue);
              }
            }}
            disabled={!ifNoAddresses}
            renderInput={(params) => (
              <TextInput
                {...params}
                placeholder={'Select Address Type'}
                label="Address Type"
                errorMessage={getErrorMessage(form.touched.address_type, form.errors.address_type)}
                inputProps={{
                  ...params.inputProps,
                  autocomplete: 'none',
                  form: {
                    autocomplete: 'none',
                  },
                }}
              />
            )}
          /> */}
            <TextInput
              variant="outlined"
              autoComplete="none"
              value={form.values.address1}
              placeholder="Enter Street Address"
              onChange={form.handleChange}
              name="address1"
              label="Street Address"
            />

            <Autocomplete
              options={mappedCountry(countriesData || [])}
              getOptionLabel={(option) => option.key}
              fullWidth
              getOptionSelected={(options, value) => options.value === form.values.country_code}
              classes={{ option: classes.option }}
              value={{
                value: form.values.country_code ? form.values.country_code : 'US',
                key:
                  mappedCountry(countriesData || []).find((v) => v.value === form.values.country_code)?.key ||
                  'United States',
              }}
              onChange={(e, record) => {
                if (record) {
                  form.setFieldValue('state_code', '');
                  form.setFieldValue('city_suburb', '');
                  form.setFieldValue('zip_code', '');
                  setAddressList([]);
                  return form.setFieldValue('country_code', record.value);
                }
              }}
              renderInput={(params) => (
                <TextInput
                  {...params}
                  type="text"
                  variant="outlined"
                  label="Country"
                  errorMessage={getErrorMessage(form.touched.country_code, form.errors.country_code)}
                  autoComplete="none"
                  value={form.values.country_code}
                  name="country_code"
                  placeholder="Select Country"
                  onBlur={form.handleBlur}
                  inputProps={{
                    ...params.inputProps,
                    autocomplete: 'none',

                    form: {
                      autocomplete: 'none',
                    },
                  }}
                />
              )}
            />
            <Autocomplete
              options={addressList || []}
              getOptionLabel={(option) => option.key}
              renderOption={(option) => <span>{option.address1}</span>}
              loading={isGeolocateLoading}
              value={{
                key: form.values.zip_code || '',
                address1: form.values.address1 || '',
                state_code: form.values.state_code || '',
                city_suburb: form.values.city_suburb || '',
              }}
              classes={{ option: classes.option }}
              fullWidth
              disabled={!form.values.country_code}
              onChange={(e, record) => {
                if (record) {
                  form.setFieldValue('state_code', record.state_code);
                  form.setFieldValue('city_suburb', record.city_suburb);
                  return form.setFieldValue('zip_code', record.key);
                }

                return form.handleChange;
              }}
              renderInput={(params) => (
                <TextInput
                  {...params}
                  variant="outlined"
                  autoComplete="none"
                  value={form.values.zip_code}
                  placeholder="Enter Zip Code"
                  onChange={(e) => {
                    form.setFieldValue('zip_code', e.target.value);
                    if (e.target.value.length > 3) {
                      geocode(e.target.value);
                    } else {
                      setAddressList([]);
                    }
                  }}
                  name="zip_code"
                  label="Zip Code"
                  type="number"
                  inputProps={{
                    ...params.inputProps,
                    autocomplete: 'none',

                    form: {
                      autocomplete: 'none',
                    },
                  }}
                />
              )}
            />
            <Autocomplete
              options={mappedState(statesData || [])}
              clearOnBlur={false}
              getOptionLabel={(option) => option.key}
              fullWidth
              classes={{ option: classes.option }}
              getOptionSelected={(options, values) => options.value === values.value}
              value={{
                value: form.values.state_code || '',
                key: mappedState(statesData || []).find((v) => v.value === form.values.state_code)?.key || '',
              }}
              onChange={(e, record, reason) => {
                if (record) {
                  if (reason === 'clear') {
                    form.setFieldValue('city_suburb', '');
                    return form.setFieldValue('state_code', '');
                  } else if (reason === 'select-option') {
                    form.setFieldValue('city_suburb', '');
                    return form.setFieldValue('state_code', record.value);
                  }
                }
              }}
              renderInput={(params) => (
                <TextInput
                  {...params}
                  type="text"
                  variant="outlined"
                  errorMessage={getErrorMessage(form.touched.state_code, form.errors.state_code)}
                  autoComplete="none"
                  value={form.values.state_code}
                  name="State_code"
                  label="State"
                  placeholder="Select State"
                  onBlur={form.handleBlur}
                  inputProps={{
                    ...params.inputProps,
                    autocomplete: 'none',
                    form: {
                      autocomplete: 'none',
                    },
                  }}
                />
              )}
            />

            <Autocomplete
              options={mappedCity(citiesData || [])}
              clearOnBlur={false}
              getOptionLabel={(option) => option.key}
              fullWidth
              getOptionSelected={(options, values) => options.value === values.value}
              value={{
                value: form.values.city_suburb || '',
                key: mappedCity(citiesData || []).find((v) => v.value === form.values.city_suburb)?.key || '',
              }}
              classes={{ option: classes.option }}
              onChange={(e, record, reason) => {
                if (record) {
                  if (reason === 'clear') {
                    return form.setFieldValue('city_suburb', '');
                  } else if (reason === 'select-option') {
                    return form.setFieldValue('city_suburb', record.value);
                  }
                }
              }}
              renderInput={(params) => (
                <TextInput
                  {...params}
                  type="text"
                  variant="outlined"
                  errorMessage={getErrorMessage(form.touched.city_suburb, form.errors.city_suburb)}
                  autoComplete="none"
                  value={form.values.city_suburb}
                  name="city_suburb"
                  onBlur={form.handleBlur}
                  label="City"
                  placeholder="Select City"
                  inputProps={{
                    ...params.inputProps,
                    autocomplete: 'none',
                    form: {
                      autocomplete: 'none',
                    },
                  }}
                />
              )}
            />
          </Content>
        ) : (
          <WorkingLocationModal data={data} form={form} country={country} />
        )}
        <ActionsContainer>
          <CustomButton
            data-cy="save-location"
            fullWidth
            variant="contained"
            color="primary"
            onClick={() => form.handleSubmit()}
            disabled={updateIsLoading || isLoading || !form.values.country_code || !form.values.city_suburb}
            style={{ background: updateIsLoading || isLoading ? COLORS.DARK_GRAY : COLORS.PRIMARY }}
          >
            {updateIsLoading || isLoading ? <CircularProgress size={20} /> : data ? 'Save' : 'Add'}
          </CustomButton>
          {/* <CustomButton fullWidth onClick={() => onClose(false)}>
            Cancel
          </CustomButton> */}
        </ActionsContainer>
      </CustomDialogContent>

      {/* <Backdrop isLoading={isLoading ? isLoading : updateIsLoading ? updateIsLoading : false} /> */}
    </CustomDialog>
  );
};

export default LocationModal;
